
import { useState, useCallback } from "react";
import { Investor } from "@/types/investor";
import { toast } from "sonner";
import { 
  processBatch,
  enrichSingleInvestor,
  recoverFromBatchFailure
} from "@/utils/investors/processingUtils";
import { getProviderToken } from "@/utils/emailUtils";
import { markStateFresh } from "@/utils/investors/processing/processingState";
import { getAPIKey } from "@/utils/ai/apiKeyUtils";

export const useInvestorAI = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isProcessingBatch, setIsProcessingBatch] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);

  const handleRelevanceSubmit = async (
    context: string, 
    investors: Investor[], 
    processedIds: Set<string>,
    onUpdateInvestors: (updated: Investor[]) => void,
    onUpdateProcessedIds: (updated: Set<string>) => void
  ) => {
    // Get OpenAI API key from localStorage/providers
    const openAiKey = getAPIKey("openai");
    
    if (!openAiKey) {
      toast.error("Please configure your OpenAI API key in the API Connections section");
      return;
    }

    if (investors.length === 0) {
      toast.error("Please upload investor data first");
      return;
    }

    try {
      setIsAnalyzing(true);
      
      // Process first 5 investors immediately as a sample
      const batchSize = Math.min(5, investors.length);
      
      // Processing with immediate database updates for each investor
      const updatedInvestors = await processBatch(
        batchSize,
        investors,
        processedIds,
        context,
        openAiKey,
        async (processedInvestor) => {
          // Create a new array with the processed investor
          const newInvestors = investors.map(inv => 
            inv.id === processedInvestor.id ? processedInvestor : inv
          );
          
          // Update the state immediately with each processed investor
          onUpdateInvestors(newInvestors);
          
          // Add to processed IDs
          const updatedProcessedIds = new Set(processedIds);
          updatedProcessedIds.add(processedInvestor.id);
          onUpdateProcessedIds(updatedProcessedIds);
          
          // Mark data as fresh to prevent stale state detection
          markStateFresh();
          
          return true;
        }
      );
      
      // Final update to ensure everything is synced
      onUpdateInvestors(updatedInvestors);

      // Ensure processed IDs are up to date
      const newProcessedIds = new Set([...processedIds]);
      updatedInvestors.forEach(inv => {
        if (inv.enrichedWith) {
          newProcessedIds.add(inv.id);
        }
      });
      onUpdateProcessedIds(newProcessedIds);
      
      toast.success(`Initial ${batchSize} investors analyzed. Use batch processor to continue.`);
    } catch (error) {
      console.error("Error updating relevance scores:", error);
      toast.error("Failed to update relevance scores. Please check your API key.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handler for processing a single investor
  const handleProcessInvestor = async (
    investor: Investor, 
    relevanceContext: string,
    investors: Investor[],
    processedIds: Set<string>,
    onUpdateInvestors: (updated: Investor[]) => Promise<boolean>,
    onUpdateProcessedIds: (updated: Set<string>) => void
  ) => {
    // Get OpenAI API key from localStorage/providers
    const openAiKey = getAPIKey("openai");
    
    if (!openAiKey) {
      toast.error("Please configure your OpenAI API key in the API Connections section");
      return investor;
    }

    if (!relevanceContext) {
      toast.error("Please set relevance criteria first");
      return investor;
    }

    try {
      // Process the single investor with immediate database update
      const updatedInvestor = await enrichSingleInvestor(
        investor, 
        relevanceContext, 
        openAiKey,
        async (enrichedInvestor) => {
          // Update the investor in the investors array
          const updatedInvestors = investors.map(inv => 
            inv.id === enrichedInvestor.id ? enrichedInvestor : inv
          );
          
          // Immediately save to database - critically wait for the save operation to complete
          console.log(`Saving processed investor ${enrichedInvestor.id} to database immediately`);
          const saveSuccess = await onUpdateInvestors(updatedInvestors);
          
          if (!saveSuccess) {
            console.error(`Failed to save processed investor ${enrichedInvestor.id} to database`);
            toast.error("Failed to save to database. Please try again.");
            return false;
          }
          
          console.log(`Successfully saved processed investor ${enrichedInvestor.id} to database`);
          
          // Mark data as fresh to prevent stale state detection
          markStateFresh();
          
          return true;
        }
      );
      
      // Update processed IDs set
      const newProcessedIds = new Set([...processedIds]);
      newProcessedIds.add(updatedInvestor.id);
      onUpdateProcessedIds(newProcessedIds);
      
      toast.success(`Processed investor: ${updatedInvestor.name || updatedInvestor.firm}`);
      return updatedInvestor;
    } catch (error) {
      console.error("Error processing investor:", error);
      toast.error("Failed to process investor. Please check your API key.");
      throw error;
    }
  };

  // Handler for enriching a selected investor
  const handleEnrichInvestor = useCallback(async (
    investor: Investor,
    relevanceContext: string,
    investors: Investor[],
    onUpdateInvestors: (updated: Investor[]) => Promise<boolean>,
    onUpdateSelectedInvestor: (updated: Investor) => void
  ) => {
    // Get API keys from localStorage/providers
    const openAiKey = getAPIKey("openai");
    
    if (!openAiKey) {
      toast.error("Please configure your API keys in the API Connections section");
      return investor;
    }

    if (!relevanceContext) {
      toast.error("Please set relevance criteria first");
      return investor;
    }

    try {
      setIsEnriching(true);
      
      // Set strict mode for re-enrich actions to disable OpenAI fallback
      localStorage.setItem('enrich_strict_mode', 'true');
      
      // Call enrichSingleInvestor with a callback to immediately save to database after enrichment
      const enrichedInvestor = await enrichSingleInvestor(
        investor,
        relevanceContext,
        openAiKey,
        async (updatedInvestor) => {
          // Immediately save the enriched investor to database
          console.log(`Saving enriched investor ${updatedInvestor.id} to database`);
          
          // Update the investors array with the enriched investor
          const updatedInvestors = investors.map(inv => 
            inv.id === updatedInvestor.id ? updatedInvestor : inv
          );
          
          // Force sync to database with this call
          const saveResult = await onUpdateInvestors(updatedInvestors);
          
          if (!saveResult) {
            console.error(`Failed to save enriched investor ${updatedInvestor.id} to database`);
            toast.error("Failed to save enrichment to database");
          } else {
            console.log(`Successfully saved enriched investor ${updatedInvestor.id} to database`);
          }
          
          // Update selected investor if needed
          onUpdateSelectedInvestor(updatedInvestor);
          
          // Mark data as fresh to prevent stale state detection
          markStateFresh();
          
          // Return save result to indicate success/failure
          return saveResult;
        }
      );
      
      return enrichedInvestor;
    } catch (error) {
      console.error("Error enriching investor:", error);
      toast.error("Failed to enrich investor data. Please check your API keys.");
      throw error;
    } finally {
      // Always clear strict mode after enrichment
      localStorage.removeItem('enrich_strict_mode');
      setIsEnriching(false);
    }
  }, []);

  // Main batch processing handler - updated for more linear approach
  const handleProcessBatch = useCallback(async (
    batchSize: number, 
    investors: Investor[],
    relevanceContext: string,
    processedIds: Set<string>,
    onUpdateInvestors: (updated: Investor[]) => Promise<boolean>,
    onUpdateProcessedIds: (updated: Set<string>) => void,
    onInvestorProcessed?: (investor: Investor) => Promise<boolean>
  ): Promise<Investor[]> => {
    // Get OpenAI API key from localStorage/providers
    const openAiKey = getAPIKey("openai");
    
    if (!openAiKey) {
      toast.error("Please configure your OpenAI API key in the API Connections section");
      return investors;
    }

    if (!relevanceContext) {
      toast.error("Please set relevance criteria first");
      return investors;
    }

    // Recover from any previous failed batch processing
    recoverFromBatchFailure();

    try {
      setIsProcessingBatch(true);
      
      // Count unprocessed investors
      // Ensure processedIds is a Set
      const safeProcessedIds = processedIds instanceof Set ? 
        processedIds : new Set(Array.isArray(processedIds) ? processedIds : []);
      
      const unprocessedCount = investors.filter(inv => !safeProcessedIds.has(inv.id)).length;
      
      if (unprocessedCount === 0) {
        toast.info("All investors have been processed!");
        return investors;
      }
      
      const actualBatchSize = Math.min(batchSize, unprocessedCount);
      
      toast.loading(`Processing ${actualBatchSize} of ${unprocessedCount} remaining investors...`);
      
      console.log(`Starting batch processing of ${actualBatchSize} investors`);
      
      // Sequential approach to processing each investor in the batch
      const saveInvestorCallback = async (processedInvestor: Investor): Promise<boolean> => {
        if (!processedInvestor || !processedInvestor.id) {
          console.error("Invalid investor data received for saving");
          return false;
        }
        
        console.log(`Saving individual investor ${processedInvestor.id} to database`);
        
        try {
          // Update the investor in the array
          const updatedInvestorsArray = investors.map(inv => 
            inv.id === processedInvestor.id ? processedInvestor : inv
          );
          
          // BLOCKING CALL - Save to database immediately and wait for it to complete
          const success = await onUpdateInvestors(updatedInvestorsArray);
          
          if (success) {
            // Ensure it's added to processed IDs
            const updatedProcessedIds = new Set(safeProcessedIds);
            updatedProcessedIds.add(processedInvestor.id);
            
            // Update processed IDs in parent component
            onUpdateProcessedIds(updatedProcessedIds);
            
            // Mark data as fresh to prevent stale state detection
            markStateFresh();
            
            // Call the custom callback if provided
            if (onInvestorProcessed) {
              // BLOCKING CALL - Wait for the callback to complete
              return await onInvestorProcessed(processedInvestor);
            }
            
            return true;
          } else {
            console.error(`Failed to save investor ${processedInvestor.id} to database`);
            return false;
          }
        } catch (error) {
          console.error(`Error saving investor ${processedInvestor.id}:`, error);
          return false;
        }
      };
      
      // Call batch processing with our sequential callback
      // BLOCKING CALL - Process batch one by one
      const updatedInvestors = await processBatch(
        actualBatchSize,
        investors,
        safeProcessedIds,
        relevanceContext,
        openAiKey,
        saveInvestorCallback
      );
      
      // Final save of the entire updated array - BLOCKING CALL
      console.log(`Final save of all ${updatedInvestors.length} investors after batch processing`);
      await onUpdateInvestors(updatedInvestors);
      
      // Update processed IDs one final time
      const finalProcessedIds = new Set(safeProcessedIds);
      updatedInvestors.forEach(investor => {
        if (investor.enrichedWith) {
          finalProcessedIds.add(investor.id);
        }
      });
      onUpdateProcessedIds(finalProcessedIds);
      
      // Mark data as fresh to prevent stale state detection
      markStateFresh();
      
      toast.success(`Processed batch of ${actualBatchSize} investors`);
      return updatedInvestors;
    } catch (error) {
      console.error("Error processing batch:", error);
      toast.error("Failed to process batch. Please check your API key.");
      return investors;
    } finally {
      setIsProcessingBatch(false);
    }
  }, []);

  // Return hooks and functions
  return {
    isAnalyzing,
    isProcessingBatch,
    isEnriching,
    setIsEnriching,
    handleRelevanceSubmit,
    handleProcessInvestor,
    handleEnrichInvestor,
    handleProcessBatch,
  };
};
